import React from "react";
import { Container, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{ height: "40px", cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
        <div>
          <Button color="inherit" onClick={() => navigate("/")}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate("/login")}>
            Sign In
          </Button>
          <Button color="inherit" onClick={() => navigate("/help")}>
            Help
          </Button>
        </div>
      </header>

{/* Content */}
      <Container
        maxWidth="md"
        style={{
          marginTop: "30px",
          marginBottom: "30px",
          background: "rgba(255, 255, 255, 0.97)",
          borderRadius: "10px",
          padding: "30px 20px",
          color: "#333",
        }}
      >
      <Typography variant="h4" gutterBottom style={{ fontWeight: "bold" }}>
        Privacy Policy
      </Typography>

      <Typography variant="body2" paragraph>
        Last Updated: December 2025
      </Typography>

      {/* 1. Who we are */}
      <Typography variant="h5" gutterBottom>
        1. Who We Are
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld is a digital storytelling and reading platform designed
        for children, families, and schools. We provide AI-assisted story
        generation, phonics-aligned reading support, comprehension questions,
        student voice recordings, and analytics dashboards for parents, teachers
        and schools.
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld is operated by:
        <br />
        <strong>YOURSTORYWORLD LTD</strong>
        <br />
        Company Number: <strong>17098564</strong> (United Kingdom)
      </Typography>

      {/* 2. Scope */}
      <Typography variant="h5" gutterBottom>
        2. Scope of this Policy
      </Typography>
      <Typography variant="body1" paragraph>
        This Privacy Policy explains how we collect, use, store and protect
        personal data when:
      </Typography>
      <ul>
        <li>Parents or carers register a family or individual account</li>
        <li>Schools use YourStoryWorld with pupils and staff</li>
        <li>Children use the platform under the supervision of an adult</li>
        <li>Visitors access our website or support pages</li>
      </ul>

      <Typography variant="body1" paragraph>
        This Policy should be read together with our{" "}
        <a href="/terms">Terms of Use</a>,{" "}
        <a href="/cookie-policy">Cookie Policy</a>,{" "}
        <a href="/child-safety">Child Safety & Safeguarding</a> page and our{" "}
        <a href="/schools-data-protection">
          Data Protection &amp; Security for Schools
        </a>{" "}
        information (where applicable).
      </Typography>

      {/* 3. Who is the Data Controller? */}
      <Typography variant="h5" gutterBottom>
        3. Who is the Data Controller?
      </Typography>
      <Typography variant="body1" paragraph>
        The data controller depends on how YourStoryWorld is being used:
      </Typography>
      <ul>
        <li>
          <strong>Families / Individual Accounts:</strong> For parents and
          carers who sign up directly,{" "}
          <strong>YOURSTORYWORLD LTD</strong> is the data controller for the
          account and associated usage data.
        </li>
        <li>
          <strong>Schools / Multi-Academy Trusts:</strong> When YourStoryWorld
          is used under a school or trust subscription, the{" "}
          <strong>school or trust</strong> is the data controller for pupil
          data. EZAA EDUPLATFORM LTD acts as a{" "}
          <strong>data processor</strong>, processing personal data on the
          school’s documented instructions and under a suitable data processing
          agreement (DPA).
        </li>
      </ul>

      {/* 4. What data we collect */}
      <Typography variant="h5" gutterBottom>
        4. What Data We Collect
      </Typography>

      <Typography variant="subtitle1" gutterBottom>
        4.1 Account & Profile Information
      </Typography>
      <ul>
        <li>Parent / carer name and email address</li>
        <li>Teacher name, role and school email address</li>
        <li>
          School or organisation details (school name, class name, year group
          and similar identifiers)
        </li>
        <li>
          Child or pupil first name, nickname or pseudonym (as configured by
          the parent or school)
        </li>
      </ul>

      <Typography variant="subtitle1" gutterBottom>
        4.2 Learning & Usage Data
      </Typography>
      <ul>
        <li>Stories generated and read by the user</li>
        <li>Phonics mode usage and reading prompts</li>
        <li>Time spent reading or listening to stories</li>
        <li>Comprehension questions answered and scores achieved</li>
        <li>Teacher-assigned themes and completion status</li>
        <li>
          Progress analytics such as streaks, genres explored and reading
          trends
        </li>
      </ul>

      <Typography variant="subtitle1" gutterBottom>
        4.3 Voice Recordings (Optional)
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld allows children to record themselves reading stories and
        (optionally) submit these recordings to their teacher or save them to
        their dashboard. Where this feature is enabled:
      </Typography>
      <ul>
        <li>We store audio files securely in cloud storage</li>
        <li>
          We link the recording to the relevant student profile and story for
          feedback and assessment
        </li>
        <li>
          Schools or parents can request deletion of voice recordings at any
          time
        </li>
      </ul>

      <Typography variant="subtitle1" gutterBottom>
        4.4 Technical & Log Data
      </Typography>
      <ul>
        <li>IP address, device type, operating system, browser type</li>
        <li>
          Usage logs (such as login time, error logs and performance data) for
          security, diagnostics and service reliability
        </li>
        <li>
          Cookie data and similar technologies (see our{" "}
          <a href="/cookie-policy">Cookie Policy</a>)
        </li>
      </ul>

      <Typography variant="subtitle1" gutterBottom>
        4.5 Special Category Data
      </Typography>
      <Typography variant="body1" paragraph>
        We do not intentionally collect or require special category data (such
        as health, religious or biometric data). The Service is designed for
        reading and educational use only. Schools should avoid entering
        sensitive information into free-text fields.
      </Typography>

      {/* 5. How we use data */}
      <Typography variant="h5" gutterBottom>
        5. How We Use Personal Data
      </Typography>
      <Typography variant="body1" paragraph>
        We use personal data only to deliver, maintain and improve the
        YourStoryWorld service, for example:
      </Typography>
      <ul>
        <li>Creating personalised, age-appropriate AI stories and prompts</li>
        <li>
          Adapting reading material, vocabulary and phonics support to the
          user’s age and settings
        </li>
        <li>
          Generating comprehension questions and tracking understanding over
          time
        </li>
        <li>
          Powering dashboards and reports for parents, teachers and schools
        </li>
        <li>
          Enabling optional features such as voice recording and teacher
          feedback
        </li>
        <li>
          Monitoring usage to keep the service secure, stable and reliable
        </li>
        <li>
          Responding to support requests and technical issues raised via our
          contact channels
        </li>
      </ul>

      <Typography variant="body2" paragraph>
        We <strong>do not</strong>:
      </Typography>
      <ul>
        <li>Sell personal data to third parties</li>
        <li>Use children’s data for behavioural advertising</li>
        <li>
          Allow open social networking features where children can talk to
          strangers
        </li>
      </ul>

      {/* 6. Legal basis */}
      <Typography variant="h5" gutterBottom>
        6. Legal Basis for Processing
      </Typography>
      <Typography variant="body1" paragraph>
        Under UK GDPR and similar laws, we rely on different legal bases
        depending on the context:
      </Typography>
      <ul>
        <li>
          <strong>Families / individual accounts:</strong> consent (for
          creating an account and enabling optional features like recordings)
          and legitimate interests (to provide a safe, educational reading
          platform).
        </li>
        <li>
          <strong>Schools:</strong> public task or legitimate educational
          interest as determined by the school or trust, with YourStoryWorld
          acting as data processor under a DPA.
        </li>
        <li>
          <strong>Security and fraud prevention:</strong> legitimate interests.
        </li>
        <li>
          <strong>Legal obligations:</strong> where we must keep certain records
          for regulatory or audit purposes.
        </li>
      </ul>

      {/* 7. AI & content */}
      <Typography variant="h5" gutterBottom>
        7. AI, Content Generation & Safety Measures
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld uses AI to generate stories, prompts and questions based
        on user inputs (e.g. names, interests, themes).  We use AI models to generate personalised stories. YourStoryWorld includes strict safety filters designed to prevent harmful or inappropriate content from being shown to children. Stories containing violence, explicit themes, strong language, discrimination, or adult material are 
  automatically rewritten or blocked. No personal data is used to generate stories.  To maintain safety and
        appropriateness:
      </Typography>
      <ul>
        <li>
          We apply content guidelines and system prompts to reduce harmful or
          inappropriate outputs.
        </li>
        <li>
          We do not use children’s personal data to train public AI models in a
          way that would allow other customers to access such data.
        </li>
        <li>
          We perform technical and manual checks on the platform to monitor for
          misuse and improve our safety systems over time.
        </li>
      </ul>
      <Typography variant="body1" paragraph>
        Our <a href="/child-safety">Child Safety & Safeguarding</a> page gives
        additional details on how we design YourStoryWorld with children’s
        safety and wellbeing in mind.
      </Typography>

      {/* 8. Cookies */}
      <Typography variant="h5" gutterBottom>
        8. Cookies and Similar Technologies
      </Typography>
      <Typography variant="body1" paragraph>
        We use cookies and similar technologies to:
      </Typography>
      <ul>
        <li>Keep users logged in securely</li>
        <li>Remember basic preferences (e.g. language, age group settings)</li>
        <li>Measure performance and usage patterns to improve the service</li>
      </ul>
      <Typography variant="body1" paragraph>
        You can manage cookies via your browser settings and through our{" "}
        <a href="/cookie-policy">Cookie Policy</a>, which explains the types of
        cookies we use and your choices, including “Accept essential only”.
      </Typography>

      {/* 9. Sharing data */}
      <Typography variant="h5" gutterBottom>
        9. When We Share Your Data
      </Typography>
      <Typography variant="body1" paragraph>
        We only share personal data where necessary and appropriate, for
        example:
      </Typography>
      <ul>
        <li>
          With <strong>trusted service providers</strong> who help us operate
          the platform (e.g. cloud hosting, secure storage, analytics). These
          providers act as data processors and are bound by contract to protect
          personal data.
        </li>
        <li>
          With <strong>schools or trusts</strong> that manage pupil accounts, to
          provide dashboards and reports to authorised staff.
        </li>
        <li>
          With <strong>payment providers</strong> (for parent subscriptions)
          where necessary to process payments securely.
        </li>
        <li>
          Where we are required to do so by law, regulation or a valid legal
          request from authorities.
        </li>
      </ul>
      <Typography variant="body1" paragraph>
        We do not sell personal data or share children’s data with advertisers.
      </Typography>

      {/* 10. Data storage & international transfers */}
      <Typography variant="h5" gutterBottom>
        10. Data Storage & International Transfers
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld is primarily hosted using reputable cloud providers
        (e.g. Google Firebase and equivalent infrastructure). Data may be stored
        or processed in the United Kingdom, the European Economic Area (EEA),
        and, where necessary, other countries that provide appropriate
        safeguards.
      </Typography>
      <Typography variant="body1" paragraph>
        Where data is transferred outside the UK or EEA, we use appropriate
        safeguards such as:
      </Typography>
      <ul>
        <li>Standard Contractual Clauses (SCCs)</li>
        <li>Data processing addenda with our suppliers</li>
        <li>Technical safeguards such as encryption in transit and at rest</li>
      </ul>

      {/* 11. Security */}
      <Typography variant="h5" gutterBottom>
        11. How We Keep Data Secure
      </Typography>
      <Typography variant="body1" paragraph>
        We take technical and organisational measures to protect personal data,
        including:
      </Typography>
      <ul>
        <li>Encryption of data in transit (HTTPS) and at rest</li>
        <li>Access controls and role-based permissions</li>
        <li>Secure authentication for admin and teacher accounts</li>
        <li>Activity logging and monitoring for suspicious behaviour</li>
        <li>Regular reviews of our security configuration</li>
      </ul>
      <Typography variant="body1" paragraph>
        In the event of a data breach that poses a risk to individuals’ rights
        and freedoms, we will follow our incident response process, notify
        affected schools or users where appropriate and, if required, notify the
        UK Information Commissioner’s Office (ICO) or relevant supervisory
        authority.
      </Typography>

      {/* 12. Retention */}
      <Typography variant="h5" gutterBottom>
        12. Data Retention
      </Typography>
      <Typography variant="body1" paragraph>
        We keep personal data only for as long as reasonably necessary for the
        purposes described in this Policy or as required by law. In particular:
      </Typography>
      <ul>
        <li>
          <strong>Family accounts:</strong> data is retained while the account
          remains active. If you delete your account or request deletion, we
          will remove or anonymise personal data within a reasonable period,
          subject to any legal obligations to retain certain records.
        </li>
        <li>
          <strong>School accounts and pupil data:</strong> data is retained for
          the duration of the school’s subscription and a short agreed period
          afterwards (for example, to allow transfers or end-of-year tasks), or
          as otherwise set out in the school’s data processing agreement.
        </li>
        <li>
          <strong>Voice recordings:</strong> may be deleted earlier at the
          request of the school, parent or user, or automatically after a
          defined retention period if configured.
        </li>
        <li>
          <strong>Aggregated, anonymised data:</strong> we may retain aggregated
          analytics that no longer identify individual users, to help improve
          our service over time.
        </li>
      </ul>

      {/* 13. Children’s privacy */}
      <Typography variant="h5" gutterBottom>
        13. Children’s Privacy
      </Typography>
      <Typography variant="body1" paragraph>
        YourStoryWorld is designed for use by children under the supervision of
        adults (parents, carers or educators). We:
      </Typography>
      <ul>
        <li>Require adult-managed accounts for children</li>
        <li>
          Do not allow children to sign up directly without an adult or school
          creating their account
        </li>
        <li>
          Encourage schools and parents to use first names or pseudonyms rather
          than full names wherever possible
        </li>
      </ul>
      <Typography variant="body1" paragraph>
        If we become aware that we have collected personal data directly from a
        child contrary to this Policy, we will take reasonable steps to delete
        it and, where appropriate, notify the school or parent.
      </Typography>

      {/* 14. Your rights */}
      <Typography variant="h5" gutterBottom>
        14. Your Data Protection Rights
      </Typography>
      <Typography variant="body1" paragraph>
        Depending on your location and circumstances, you may have rights under
        data protection laws, including:
      </Typography>
      <ul>
        <li>Right of access – to request a copy of your personal data</li>
        <li>Right to rectification – to correct inaccurate information</li>
        <li>Right to erasure – to request deletion in certain circumstances</li>
        <li>
          Right to restrict processing – to limit how your data is used in some
          cases
        </li>
        <li>
          Right to object – to certain types of processing, such as direct
          marketing (we do not market to children)
        </li>
        <li>
          Right to data portability – to receive your data in a common format in
          some cases
        </li>
      </ul>
      <Typography variant="body1" paragraph>
        Where YourStoryWorld is used via a school subscription, many of these
        rights will be exercised through the school as the data controller (for
        example, access or deletion of pupil records).
      </Typography>

      {/* 15. Contact & complaints */}
      <Typography variant="h5" gutterBottom>
        15. Contact Us & Complaints
      </Typography>
      <Typography variant="body1" paragraph>
        If you have any questions, concerns or requests about this Privacy
        Policy or how we handle personal data, you can contact us at:
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Email:</strong> support@yourstoryworld.com
        <br />
        <strong>Company:</strong> YOURSTORYWORLD LTD (UK)
        <br />
        Company Number: 17098564
      </Typography>
      <Typography variant="body1" paragraph>
        You also have the right to lodge a complaint with your local data
        protection authority. In the UK, this is the{" "}
        <a
          href="https://ico.org.uk/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Information Commissioner&apos;s Office (ICO)
        </a>
        .
      </Typography>

      {/* 16. Changes */}
      <Typography variant="h5" gutterBottom>
        16. Changes to this Policy
      </Typography>
      <Typography variant="body1" paragraph>
        We may update this Privacy Policy from time to time to reflect changes
        in technology, law or our services. When we make significant changes, we
        will take appropriate steps to notify you (for example, via the website
        or by email) and update the “Last Updated” date at the top of this page.
      </Typography>
    </Container>
  </div>
  );
};

export default PrivacyPolicy;
